import React, { useState } from 'react';

/**
 * Modal for resolving upload conflicts when iso_sheet_number already exists in database
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - conflicts: Array of { iso_sheet_number, existing_rows: [...] }
 * - onResolve: (resolution: { strategy, decisions }) => void
 */
const ConflictResolutionModal = ({ isOpen, onClose, conflicts, onResolve }) => {
  const [strategy, setStrategy] = useState(''); // 'replace_all', 'skip_all', 'per_row'
  const [perRowDecisions, setPerRowDecisions] = useState({});
  
  // Initialize per-row decisions when conflicts change
  React.useEffect(() => {
    if (conflicts && conflicts.length > 0) {
      const initial = {};
      conflicts.forEach(c => {
        initial[c.iso_sheet_number] = 'replace'; // Default to replace
      });
      setPerRowDecisions(initial);
    }
  }, [conflicts]);
  
  const handleStrategyChange = (newStrategy) => {
    setStrategy(newStrategy);
  };
  
  const handlePerRowDecision = (isoSheetNumber, decision) => {
    setPerRowDecisions(prev => ({
      ...prev,
      [isoSheetNumber]: decision
    }));
  };
  
  const handleConfirm = () => {
    if (!strategy) return;
    
    if (strategy === 'per_row') {
      onResolve({
        strategy: 'per_row',
        decisions: perRowDecisions
      });
    } else {
      onResolve({ strategy });
    }
  };
  
  if (!isOpen || !conflicts || conflicts.length === 0) return null;
  
  const totalConflicts = conflicts.length;
  const replaceCount = strategy === 'per_row' 
    ? Object.values(perRowDecisions).filter(d => d === 'replace').length 
    : (strategy === 'replace_all' ? totalConflicts : 0);
  const skipCount = strategy === 'per_row'
    ? Object.values(perRowDecisions).filter(d => d === 'skip').length
    : (strategy === 'skip_all' ? totalConflicts : 0);
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1100
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '1.5rem',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <h3 style={{ margin: 0, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ⚠️ Duplicate Records Found
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Warning message */}
        <div style={{
          backgroundColor: '#fff3e0',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          border: '1px solid #ffb74d'
        }}>
          <p style={{ margin: 0, color: '#e65100', fontWeight: 500 }}>
            {totalConflicts} row(s) in your upload have <strong>ISO Sheet Numbers</strong> that already exist in the database.
          </p>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
            Choose how to handle these duplicates:
          </p>
        </div>
        
        {/* Strategy Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Replace All Option */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '1rem',
              border: `2px solid ${strategy === 'replace_all' ? '#1976d2' : '#e0e0e0'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: strategy === 'replace_all' ? '#e3f2fd' : 'white',
              transition: 'all 0.2s'
            }}>
              <input
                type="radio"
                name="strategy"
                value="replace_all"
                checked={strategy === 'replace_all'}
                onChange={() => handleStrategyChange('replace_all')}
                style={{ marginRight: '1rem', marginTop: '0.25rem' }}
              />
              <div>
                <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: '0.25rem' }}>
                  🔄 Replace All Existing
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Update all {totalConflicts} existing records with new data from your upload.
                  Existing data will be overwritten.
                </div>
              </div>
            </label>
            
            {/* Skip All Option */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '1rem',
              border: `2px solid ${strategy === 'skip_all' ? '#388e3c' : '#e0e0e0'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: strategy === 'skip_all' ? '#e8f5e9' : 'white',
              transition: 'all 0.2s'
            }}>
              <input
                type="radio"
                name="strategy"
                value="skip_all"
                checked={strategy === 'skip_all'}
                onChange={() => handleStrategyChange('skip_all')}
                style={{ marginRight: '1rem', marginTop: '0.25rem' }}
              />
              <div>
                <div style={{ fontWeight: 600, color: '#388e3c', marginBottom: '0.25rem' }}>
                  ⏭️ Skip All Duplicates
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Keep all {totalConflicts} existing records. Only insert rows with new ISO Sheet Numbers.
                </div>
              </div>
            </label>
            
            {/* Per-Row Option */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '1rem',
              border: `2px solid ${strategy === 'per_row' ? '#7b1fa2' : '#e0e0e0'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: strategy === 'per_row' ? '#f3e5f5' : 'white',
              transition: 'all 0.2s'
            }}>
              <input
                type="radio"
                name="strategy"
                value="per_row"
                checked={strategy === 'per_row'}
                onChange={() => handleStrategyChange('per_row')}
                style={{ marginRight: '1rem', marginTop: '0.25rem' }}
              />
              <div>
                <div style={{ fontWeight: 600, color: '#7b1fa2', marginBottom: '0.25rem' }}>
                  ✏️ Choose for Each
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Review each duplicate and decide individually whether to replace or skip.
                </div>
              </div>
            </label>
          </div>
        </div>
        
        {/* Per-Row Decision Table (only shown when per_row is selected) */}
        {strategy === 'per_row' && (
          <div style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                    ISO Sheet Number
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                    Existing Line #
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
                    Shop %
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: '180px' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map((conflict, idx) => {
                  const existingRow = conflict.existing_rows[0]; // Show first existing row
                  const decision = perRowDecisions[conflict.iso_sheet_number] || 'replace';
                  return (
                    <tr key={conflict.iso_sheet_number} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e0e0e0', fontFamily: 'monospace' }}>
                        {conflict.iso_sheet_number}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                        {existingRow?.line_number || '-'}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>
                        {existingRow?.total_shop_completion != null ? `${existingRow.total_shop_completion}%` : '-'}
                      </td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handlePerRowDecision(conflict.iso_sheet_number, 'replace')}
                            style={{
                              padding: '0.4rem 0.75rem',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              backgroundColor: decision === 'replace' ? '#1976d2' : '#e0e0e0',
                              color: decision === 'replace' ? 'white' : '#666',
                              fontWeight: decision === 'replace' ? 600 : 400
                            }}
                          >
                            Replace
                          </button>
                          <button
                            onClick={() => handlePerRowDecision(conflict.iso_sheet_number, 'skip')}
                            style={{
                              padding: '0.4rem 0.75rem',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              backgroundColor: decision === 'skip' ? '#388e3c' : '#e0e0e0',
                              color: decision === 'skip' ? 'white' : '#666',
                              fontWeight: decision === 'skip' ? 600 : 400
                            }}
                          >
                            Skip
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Summary */}
        {strategy && (
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#333' }}>
              <strong>Summary:</strong> {replaceCount} row(s) will be replaced, {skipCount} row(s) will be skipped.
            </p>
          </div>
        )}
        
        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          borderTop: '1px solid #e0e0e0',
          paddingTop: '1rem'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Cancel Upload
          </button>
          <button
            onClick={handleConfirm}
            disabled={!strategy}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: strategy ? '#1976d2' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: strategy ? 'pointer' : 'not-allowed',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            Continue Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
